import { useEffect, useState, useCallback } from 'react';

/**
 * Custom hook for real-time data updates
 * Simulates WebSocket connection with polling for development
 */
export const useRealTime = (endpoint, interval = 30000) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      // In production, this would be a WebSocket connection
      // For now, we'll simulate with periodic polling
      const response = await fetch(endpoint);
      const result = await response.json();
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Set up polling interval
    const intervalId = setInterval(fetchData, interval);

    // Cleanup
    return () => clearInterval(intervalId);
  }, [fetchData, interval]);

  return { data, loading, error, lastUpdated, refetch: fetchData };
};

/**
 * Hook for real-time notifications
 */
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now(),
      timestamp: new Date(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      dismissNotification(newNotification.id);
    }, 5000);
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  }, []);

  // Simulate real-time notifications
  useEffect(() => {
    const events = [
      { type: 'new_order', title: 'הזמנה חדשה', message: 'התקבלה הזמנה חדשה #1234', priority: 'high' },
      { type: 'low_stock', title: 'מלאי נמוך', message: 'מוצר X - נותרו 3 יחידות', priority: 'medium' },
      { type: 'new_customer', title: 'לקוח חדש', message: 'לקוח חדש נרשם למערכת', priority: 'low' }
    ];

    const interval = setInterval(() => {
      if (Math.random() > 0.7) { // 30% chance of notification
        const event = events[Math.floor(Math.random() * events.length)];
        addNotification(event);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [addNotification]);

  return {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    dismissNotification
  };
};

/**
 * Hook for live dashboard stats
 */
export const useLiveDashboard = () => {
  const [liveStats, setLiveStats] = useState({
    onlineUsers: 0,
    activeOrders: 0,
    systemLoad: 0,
    responseTime: 0
  });

  useEffect(() => {
    // Simulate live system metrics
    const interval = setInterval(() => {
      setLiveStats({
        onlineUsers: Math.floor(Math.random() * 50) + 10,
        activeOrders: Math.floor(Math.random() * 20) + 5,
        systemLoad: Math.floor(Math.random() * 100),
        responseTime: Math.floor(Math.random() * 500) + 100
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return liveStats;
};
